import { http } from '@/lib/api/http';
import type {
  ApiList,
  ApiSuccess,
  RoundDto,
  GameQuestionDto,
  RandomQuestionPreview,
} from '@/shared/types/contracts';
import type { GameType, ScoringFormula, RoundStatus } from '@/shared/enums';

export type RoundsListParams = {
  status?: RoundStatus;
  host_id?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  page_size?: number;
};

export type CreateRoundPayload = {
  name: string;
  description?: string;
  game_type: GameType;
  number_of_questions: number;
  time_limit_per_question_sec?: number;
  base_score?: number;
  time_bonus_max?: number;
  scoring_formula?: ScoringFormula;
  allow_late_join_as_spectator?: boolean;
  pin_code?: string;
  rps_timer_sec?: number;
  allow_partial_score?: boolean;
};

export type UpdateRoundPayload = Partial<CreateRoundPayload>;

export type RandomQuestionsPayload = {
  count: number;
  tags?: string[];
  answer_format?: 'text' | 'multiple_choice';
  /** Trộn nhiều question_type vào round (vd: ['quiz', 'word_scramble']). Bỏ trống = chỉ random đúng game_type của round. */
  include_question_types?: Array<'quiz' | 'word_scramble'>;
};

export type SetQuestionsPayload = {
  items: Array<{
    source_question_id: string;
    order_index: number;
    time_limit_sec?: number;
    base_score?: number;
    time_bonus_max?: number;
  }>;
};

export async function listRounds(params: RoundsListParams): Promise<ApiList<RoundDto>> {
  const { data } = await http.get<ApiList<RoundDto>>('/api/rounds', { params });
  return data;
}

export async function getRoundDetail(
  id: string,
): Promise<{ round: RoundDto; questions?: GameQuestionDto[] }> {
  const { data } = await http.get<
    ApiSuccess<{ round: RoundDto; questions?: GameQuestionDto[] }>
  >(`/api/rounds/${id}`);
  return data.data;
}

export async function getLobby(): Promise<RoundDto[]> {
  const { data } = await http.get<ApiSuccess<RoundDto[]>>('/api/lobby');
  return data.data;
}

export async function createRound(payload: CreateRoundPayload): Promise<RoundDto> {
  const { data } = await http.post<ApiSuccess<RoundDto>>('/api/admin/rounds', payload);
  return data.data;
}

export async function updateRound(id: string, payload: UpdateRoundPayload): Promise<RoundDto> {
  const { data } = await http.patch<ApiSuccess<RoundDto>>(`/api/admin/rounds/${id}`, payload);
  return data.data;
}

export async function randomQuestionsForRound(
  id: string,
  payload: RandomQuestionsPayload,
): Promise<RandomQuestionPreview[]> {
  const { data } = await http.post<ApiSuccess<RandomQuestionPreview[]>>(
    `/api/admin/rounds/${id}/questions/random`,
    payload,
  );
  return data.data;
}

export async function setRoundQuestions(
  id: string,
  payload: SetQuestionsPayload,
): Promise<RoundDto> {
  const { data } = await http.put<ApiSuccess<RoundDto>>(
    `/api/admin/rounds/${id}/questions`,
    payload,
  );
  return data.data;
}

export async function openRound(id: string): Promise<RoundDto> {
  const { data } = await http.post<ApiSuccess<RoundDto>>(`/api/admin/rounds/${id}/open`);
  return data.data;
}

export async function closeRound(id: string, cancel = false): Promise<RoundDto> {
  const { data } = await http.post<ApiSuccess<RoundDto>>(`/api/admin/rounds/${id}/close`, {
    cancel,
  });
  return data.data;
}

export async function cancelRound(id: string): Promise<RoundDto> {
  const { data } = await http.post<ApiSuccess<RoundDto>>(`/api/admin/rounds/${id}/cancel`
  );
  return data.data;
}
